import React, { useMemo } from 'react';
import {
  getPasswordRuleStates,
  type PasswordRuleId,
} from '_helpers/passwordValidation';

const RULE_LABEL: Record<PasswordRuleId, string> = {
  minLength: 'Password must be at least 8 characters long',
  number: 'Password must contain at least one number',
  special: 'Password must contain at least one special character',
  upper: 'Password must contain at least one uppercase letter',
  lower: 'Password must contain at least one lowercase letter',
};

interface PasswordRequirementsProps {
  password: string;
  confirmPassword?: string;
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  confirmPassword,
}) => {
  const rules = useMemo(() => getPasswordRuleStates(password), [password]);
  const passwordsMatch = !!password && password === confirmPassword;

  return (
    <div
      className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2"
      aria-label="Password requirements"
    >
      <p className="text-xs font-semibold text-gray-900 mb-0.5">
        Password requirements
      </p>
      {rules.map(rule => (
        <RequirementRow
          key={rule.id}
          met={rule.met}
          label={RULE_LABEL[rule.id]}
        />
      ))}
      {confirmPassword !== undefined ? (
        <RequirementRow met={passwordsMatch} label="Passwords must match" />
      ) : null}
    </div>
  );
};

function RequirementRow({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <div
        className={`mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
          met ? 'bg-green-500' : 'border-2 border-gray-300 bg-transparent'
        }`}
        aria-label={met ? 'Requirement met' : 'Requirement not met'}
      >
        {met ? (
          <span className="text-[10px] font-bold leading-3 text-white">✓</span>
        ) : null}
      </div>
      <span
        className={`flex-1 text-xs leading-4 ${met ? 'text-green-700' : 'text-gray-500'}`}
      >
        {label}
      </span>
    </div>
  );
}

export default PasswordRequirements;
